/* eslint-disable react-hooks/exhaustive-deps */
import {Grid, RadioGroup} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {AutoCompleteWithValue} from 'commons/AutoComplete/AutoCompleteWithValue';
import {CustomRadio} from 'commons/CustomRadio/CustomRadio';
import {DialogType} from 'commons/Dialog/CustomDialog';
import {localize} from 'commons/LocalizedText/LocalizedText';
import {useDialog} from 'hook/DialogProvider';
import useFetch from 'hook/UseFetch';
import {useQuery} from 'hook/UseQuery';
import useQueryState from 'hook/UseQueryState';
import {useUserInfo} from 'hook/UseUserInfo';
import * as PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {ProgramService} from 'services/ProgramService';
import {ReferenceYearService} from 'services/ReferenceYearService';
import {CAMPAIGN_FILTER} from 'utils/constants/campains';
import {ErrorName} from 'utils/constants/error.name';
import {ParamTypes} from 'utils/constants/param.type';
import {isSuperAdmin} from 'utils/constants/roles';
import {GENERATE_ROUTE, ROUTES} from 'utils/constants/routes';
import {QueryStringToJSONObject} from 'utils/helpers/QueryStringHelper';
import {setQueryStringValue} from 'utils/processing/url';

const useStyles = makeStyles({
  marginTop: {
    marginTop: 90
  },
  years: {
    maxWidth: 800,
    minWidth: 800
  },
  program: {
    maxWidth: 200
  }
});

export const SELECTED_YEARS_QUERY_KEY = 'selectedYears';
export const SELECTED_PROGRAM_QUERY_KEY = 'selectedProgram';

export const SearchBar = ({
  displayRadioYearsFilter = true,
  handleOnSearch = null,
  setIsLoadingYearsOrPrograms,
  isPreloadingData=false
}) => {
  const classes = useStyles();
  const { role } = useUserInfo();
  const query = useQuery();
  const showDialog = useDialog();
  const history = useHistory();
  const [programsOptions, setProgramsOptions] = useState([]);
  const [yearsOptions, setYearsOptions] = useState([]);
  let queryObject = QueryStringToJSONObject(window.location.search.substring(1));
  const [selectedYears, setSelectedYears] = useQueryState(
    SELECTED_YEARS_QUERY_KEY,
    queryObject?.[SELECTED_YEARS_QUERY_KEY]?.map(y => y.toString()) || [],
    ParamTypes.ARRAY
  );
  const [selectedProgram, setSelectedProgram] = useQueryState(
    SELECTED_PROGRAM_QUERY_KEY,
    queryObject?.[SELECTED_PROGRAM_QUERY_KEY],
    ParamTypes.STRING
  );
  const [campaignFilter, setCampaignFilter] = useState(isPreloadingData ? CAMPAIGN_FILTER.SPECIFIC_YEARS : CAMPAIGN_FILTER.ONGOING_CAMPAIGNS);
  const [isLoadingYears, setIsLoadingYears] = useState(false);

  const { laboratory: laboratoryId } = useParams();

  const {
    response: programsResponse,
    isLoading: isLoadingPrograms
  } = useFetch(() => ProgramService.getProgramsByLaboratoryId(laboratoryId), []);

  useEffect(() => {
    setIsLoadingYearsOrPrograms && setIsLoadingYearsOrPrograms(isLoadingYears || isLoadingPrograms);
  }, [isLoadingYears, isLoadingPrograms]);

  const isYearsArraysEqual = (oldArray, newArray) => {
    let result = oldArray.length === newArray.length;
    if (!result)
      return result;
    oldArray.forEach(oldYear => {
      if(newArray.find(newYear => newYear === oldYear) === undefined){
        result = false;
      }
    })
    return result;
  }

  useEffect(() => {
    if (!isLoadingPrograms) {
      setIsLoadingYears(true);
      ReferenceYearService.getReferenceYearsByLaboratoryIdAndProgramId(
        laboratoryId,
        programsOptions.find(p => p.text === selectedProgram)?.recid || null
      )
        .then(resp => {
          if (!isPreloadingData && selectedYears.length > 0) {
            const newYears = [...resp.filter(r => selectedYears.includes(r.year))
              .map(r => r.year)];
            if(!isYearsArraysEqual(selectedYears, newYears)){
              handleChangeYears(newYears);
            }
          }
          setYearsOptions(resp);
          setIsLoadingYears(false);
        })
        .catch(errorMessage => {
          if (errorMessage !== ErrorName.ABORT_ERROR) {
            showDialog({
              variant: DialogType.ERROR,
              title: localize('error'),
              description: errorMessage
            });
            setIsLoadingYears(false);
            history.push(GENERATE_ROUTE(ROUTES.ERROR), { errorMessage: errorMessage });
          }
        });
    }
  }, [selectedProgram]);

  useEffect(() => {
    if (programsResponse?.length > 0) {
      programsOptions.push(...programsResponse);
      setProgramsOptions(programsOptions);
    }
  }, [programsResponse]);

  useEffect(() => {
    if (!isPreloadingData && !isLoadingYears && !query.get('isFromHome')) {
      const allYears = yearsOptions.map((r) => String(r.year));
      const allYearsLength = allYears.length;
      let currentYearIndex = allYears.findIndex(year => year === (new Date()).getFullYear()
        .toString());
      if (campaignFilter === CAMPAIGN_FILTER.ALL_CAMPAIGNS) {
        if (!selectedYears || selectedYears.length === 0) {
          setSelectedYears(allYears);
        }
      } else if (campaignFilter === CAMPAIGN_FILTER.ONGOING_CAMPAIGNS) {
        const yearsToDisplay = [];
        if (currentYearIndex !== -1) {
          for (let i = currentYearIndex; i >= 0; i--) {
            if (allYears[i]) {
              yearsToDisplay.push(allYears[i]);
            }
          }
        } else {
          currentYearIndex = allYearsLength;
        }
        for (let i = currentYearIndex + 1; i < currentYearIndex + 3; i++) {
          if (allYears[i]) {
            yearsToDisplay.push(allYears[i]);
          }
        }
        if (!selectedYears || selectedYears.length === 0) {
          setSelectedYears(yearsToDisplay);
        }
      }
    }
  }, [campaignFilter, isLoadingYears, selectedProgram]);

  useEffect(() => {
    if (!isPreloadingData && selectedYears?.length > 0) {
      handleOnSearch(selectedProgram, [...selectedYears.map(year => year.year || year)]);
    }
  }, [selectedProgram, selectedYears, isPreloadingData]);

  const handleChangeYears = newYears => {
    setQueryStringValue('isFromHome', null);
    setSelectedYears([...newYears.map(year => year.year || year)]);
    if ((campaignFilter === CAMPAIGN_FILTER.ALL_CAMPAIGNS || campaignFilter === CAMPAIGN_FILTER.ONGOING_CAMPAIGNS) && selectedYears.length > 0) {
      setCampaignFilter(CAMPAIGN_FILTER.SPECIFIC_YEARS);
    }
  };

  const handleChangeProgram = newProgram => {
    setQueryStringValue('isFromHome', null);
    setSelectedProgram(newProgram);
  };

  return (
    <Grid
      alignItems="flex-end"
      container
      direction="row"
      justifyContent="space-evenly"
      wrap="nowrap"
    >
      <Grid item>
        <AutoCompleteWithValue
          className={classes.program}
          label="program"
          onChange={value => handleChangeProgram(value)}
          options={programsOptions.map((p) => String(p.text))}
          value={selectedProgram}
        />
      </Grid>
      <Grid alignItems="center" container direction="column">
        {displayRadioYearsFilter && <Grid item>
          <RadioGroup row>
            <CustomRadio
              checked={campaignFilter === CAMPAIGN_FILTER.ONGOING_CAMPAIGNS}
              label={CAMPAIGN_FILTER.ONGOING_CAMPAIGNS}
              onClick={() => setCampaignFilter(CAMPAIGN_FILTER.ONGOING_CAMPAIGNS)}
            />
            {
              isSuperAdmin(role)
              && (
                <CustomRadio
                  checked={campaignFilter === CAMPAIGN_FILTER.ALL_CAMPAIGNS}
                  label={CAMPAIGN_FILTER.ALL_CAMPAIGNS}
                  onClick={() => setCampaignFilter(CAMPAIGN_FILTER.ALL_CAMPAIGNS)}
                />
              )
            }
            <CustomRadio
              checked={campaignFilter === CAMPAIGN_FILTER.SPECIFIC_YEARS}
              label={CAMPAIGN_FILTER.SPECIFIC_YEARS}
              onClick={() => setCampaignFilter(CAMPAIGN_FILTER.SPECIFIC_YEARS)}
            />
          </RadioGroup>
        </Grid>}
        <Grid item>
          <AutoCompleteWithValue
            className={classes.years}
            error={!isLoadingYears && selectedYears.length === 0}
            getOptionLabel={option => option.year || option}
            getOptionSelected={(option, value) => option.year === value.toString()}
            helperText={localize('noYearsInStatsSearch')}
            isLoading={isLoadingYears}
            isMultiple
            label="years"
            onChange={value => handleChangeYears(value)}
            options={yearsOptions}
            value={selectedYears}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

SearchBar.propTypes = {
  setStatistics: PropTypes.func
};

SearchBar.defaultProps = {
  setStatistics: () => {
  }
};